import { useState, useEffect, useCallback } from 'react';
import { examService } from '../services/examService';

export const useExams = (params = {}) => {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const fetchExams = useCallback(async (fetchParams = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await examService.getExams({
        ...params,
        ...fetchParams
      });
      
      setExams(response.data.data.exams || []);
      
      if (response.data.data.pagination) {
        setPagination(response.data.data.pagination);
      }
    } catch (err) {
      // Handle rate limiting specifically
      if (err.response?.status === 429) {
        setError('Too many requests. Please wait a moment and refresh the page.');
        console.warn('Rate limited, stopping further requests');
        return; // Don't retry on rate limit
      }
      setError(err.response?.data?.message || 'Failed to fetch exams');
      console.error('Error fetching exams:', err);
    } finally {
      setLoading(false);
    }
  }, [params.scope, params.timeframe, params.limit, params.page, params.classId, params.subjectId, params.teacherId, params.status, params.type, params.search, params.sortBy, params.sortOrder, params.academicYear, params.term]);

  useEffect(() => {
    fetchExams();
  }, [fetchExams]);

  const createExam = async (data) => {
    try {
      const response = await examService.createExam(data);
      await fetchExams(); // Refresh the list
      return response.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to create exam';
    }
  };

  const updateExam = async (id, data) => {
    try {
      const response = await examService.updateExam(id, data);
      await fetchExams(); // Refresh the list
      return response.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to update exam';
    }
  };

  const deleteExam = async (id) => {
    try {
      await examService.deleteExam(id);
      await fetchExams(); // Refresh the list
    } catch (err) {
      throw err.response?.data?.message || 'Failed to delete exam';
    }
  };

  const scheduleExam = async (id, data) => {
    try {
      const response = await examService.scheduleExam(id, data);
      await fetchExams(); // Refresh the list
      return response.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to schedule exam';
    }
  };

  const submitExamAnswers = async (examId, data) => {
    try {
      const response = await examService.submitExamAnswers(examId, data);
      await fetchExams(); // Refresh the list
      return response.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to submit exam answers';
    }
  };

  const enterExamResults = async (examId, data) => {
    try {
      const response = await examService.enterExamResults(examId, data);
      await fetchExams(); // Refresh the list
      return response.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to enter exam results';
    }
  };

  const refreshExams = () => {
    fetchExams();
  };

  const loadMore = () => {
    if (pagination.page < pagination.totalPages) {
      fetchExams({ page: pagination.page + 1 });
    }
  };

  return {
    exams,
    loading,
    error,
    pagination,
    createExam,
    updateExam,
    deleteExam,
    scheduleExam,
    submitExamAnswers,
    enterExamResults,
    refreshExams,
    loadMore,
    fetchExams
  };
};

export const useExam = (id) => {
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasAttempted, setHasAttempted] = useState(false);
  const [attemptLoading, setAttemptLoading] = useState(true);

  const fetchExam = useCallback(async () => {
    console.log('useExam - id:', id);
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await examService.getExam(id);
      console.log('useExam - Full API Response:', response);
      console.log('useExam - Response data:', response.data);
      console.log('useExam - Response data.data:', response.data.data);
      console.log('useExam - Response data.data.exam:', response.data.data.exam);
      console.log('useExam - Setting exam to:', response.data.data.exam);
      setExam(response.data.data.exam);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch exam');
      console.error('Error fetching exam:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const checkExamAttempt = useCallback(async () => {
    if (!id) return;
    
    try {
      setAttemptLoading(true);
      const response = await examService.getMyExamResults({ examId: id });
      const results = response.data.data || [];
      setHasAttempted(results.length > 0);
    } catch (err) {
      console.error('Error checking exam attempt:', err);
      setHasAttempted(false);
    } finally {
      setAttemptLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchExam();
    checkExamAttempt();
  }, [fetchExam, checkExamAttempt]);

  console.log('useExam - Returning:', { exam, loading, error, hasAttempted, attemptLoading });
  
  return {
    exam,
    loading,
    error,
    hasAttempted,
    attemptLoading,
    refreshExam: fetchExam
  };
};

export const useExamResults = (examId) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchResults = useCallback(async () => {
    if (!examId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await examService.getExamResults(examId);
      setResults(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch exam results');
      console.error('Error fetching exam results:', err);
    } finally {
      setLoading(false);
    }
  }, [examId]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  return {
    results,
    loading,
    error,
    refreshResults: fetchResults
  };
};

export const useUpcomingExams = (params = {}) => {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUpcomingExams = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await examService.getUpcomingExams(params);
      setExams(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch upcoming exams');
      console.error('Error fetching upcoming exams:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUpcomingExams(params);
  }, [fetchUpcomingExams]);

  return {
    exams,
    loading,
    error,
    refreshUpcomingExams: fetchUpcomingExams
  };
};
